import { useState, useEffect, useRef, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import PromoLandingPage from "./PromoLandingPage";
import AdminPage from "./AdminPage";
import PrivacyPolicyPage from "./PrivacyPolicyPage";
import TermsPage from "./TermsPage";
import DeleteAccountPage from "./DeleteAccountPage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  MessageSquare, 
  Lightbulb, 
  Download, 
  QrCode, 
  FileText, 
  Building2, 
  Mail, 
  Phone, 
  Lock,
  CheckCircle,
  Clock,
  AlertCircle,
  BarChart3,
  LogOut,
  ArrowLeft,
  Send,
  Bell,
  BellRing,
  CreditCard,
  Sparkles,
  Globe,
  User,
  Save,
  Upload,
  TrendingUp,
  RefreshCw,
  Wifi
} from "lucide-react";
import { Toaster, toast } from "sonner";
import { LanguageProvider, useLanguage, languageNames } from "./LanguageContext";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell } from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

// SUCO Logo Component - Official Bulldog Logo
const SucoLogo = ({ size = 80 }) => (
  <img 
    src="https://customer-assets.emergentagent.com/job_44d3bbc8-743b-49ac-9ae6-748329023b48/artifacts/tpio1cnh_1000493039.png" 
    alt="SUCO Logo" 
    className="suco-logo"
    style={{ width: size, height: size, objectFit: 'contain' }}
  />
);

// Language Selector Component
const LanguageSelector = ({ className = "" }) => {
  const { language, setLanguage } = useLanguage();
  
  return (
    <Select value={language} onValueChange={setLanguage}>
      <SelectTrigger className={`language-selector ${className}`} data-testid="language-selector">
        <Globe className="h-4 w-4 mr-2" />
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {Object.entries(languageNames).map(([code, name]) => (
          <SelectItem key={code} value={code}>{name}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

// Landing Page
const LandingPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  return (
    <div className="landing-container">
      <LanguageSelector className="landing-lang-selector" />
      <div className="landing-content">
        <SucoLogo size={120} />
        <h1 className="landing-title">SUCO</h1>
        <p className="landing-subtitle">{t('landing_subtitle')}</p>
        <p className="landing-description">
          {t('landing_description')}
        </p>
        <div className="landing-buttons">
          <Button 
            data-testid="get-started-btn"
            className="btn-primary" 
            size="lg"
            onClick={() => navigate('/registro')}
          >
            {t('btn_get_started')}
          </Button>
          <Button 
            data-testid="login-btn"
            variant="outline" 
            size="lg"
            onClick={() => navigate('/login')}
          >
            {t('btn_have_account')}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Business Registration
const RegisterPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    password: '',
    idioma: 'es',
    custom_logo_url: ''
  });

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Solo se permiten imágenes JPG, PNG o WEBP');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('El archivo no puede superar 5MB');
      return;
    }

    setUploadingLogo(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API}/upload/logo`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData({ ...formData, custom_logo_url: `${BACKEND_URL}${response.data.logo_url}` });
      toast.success('Logo OK');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/businesses/register`, formData);
      toast.success(t('register_success'));
      
      // Store business data
      localStorage.setItem('suco_business', JSON.stringify(response.data));
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error_registering'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => navigate('/')}
          data-testid="back-btn"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <LanguageSelector />
      </div>
      
      <Card className="register-card">
        <CardHeader className="text-center">
          <SucoLogo size={60} />
          <CardTitle className="card-title">{t('register_title')}</CardTitle>
          <CardDescription>{t('register_subtitle')}</CardDescription>
        </CardHeader>
        
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="nombre">{t('business_name')}</Label>
              <div className="input-icon">
                <Building2 className="icon" />
                <Input
                  id="nombre"
                  data-testid="business-name-input"
                  placeholder="Mi Restaurante"
                  value={formData.nombre}
                  onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">{t('email')}</Label>
              <div className="input-icon">
                <Mail className="icon" />
                <Input
                  id="email"
                  type="email"
                  data-testid="email-input"
                  placeholder="email@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="telefono">{t('phone')}</Label>
              <div className="input-icon">
                <Phone className="icon" />
                <Input
                  id="telefono"
                  data-testid="phone-input"
                  placeholder="+1 555 123 4567"
                  value={formData.telefono}
                  onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">{t('password')}</Label>
              <div className="input-icon">
                <Lock className="icon" />
                <Input
                  id="password"
                  type="password"
                  data-testid="password-input"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  required
                  minLength={6}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="idioma">{t('poster_language')}</Label>
              <Select 
                value={formData.idioma}
                onValueChange={(value) => setFormData({...formData, idioma: value})}
              >
                <SelectTrigger data-testid="language-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(languageNames).map(([code, name]) => (
                    <SelectItem key={code} value={code}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t('your_logo')}</Label>
              <div className="logo-upload-container">
                <div className="logo-upload-options">
                  <label className="logo-upload-btn">
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleLogoUpload}
                      style={{ display: 'none' }}
                      data-testid="logo-file-input"
                    />
                    <Button type="button" variant="outline" disabled={uploadingLogo} asChild>
                      <span>
                        {uploadingLogo ? 'Subiendo...' : '📁 Subir archivo'}
                      </span>
                    </Button>
                  </label>
                  <span className="text-muted-foreground text-sm">{t('or')}</span>
                  <Input
                    id="custom_logo"
                    data-testid="custom-logo-input"
                    placeholder={t('logo_url')}
                    value={formData.custom_logo_url}
                    onChange={(e) => setFormData({...formData, custom_logo_url: e.target.value})}
                    className="flex-1"
                  />
                </div>
                {formData.custom_logo_url && (
                  <div className="logo-preview">
                    <img src={formData.custom_logo_url} alt="Preview" />
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setFormData({...formData, custom_logo_url: ''})}
                    >
                      ✕ {t('remove')}
                    </Button>
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground">{t('logo_hint')}</p>
            </div>
          </CardContent>
          
          <CardFooter>
            <Button 
              type="submit" 
              className="btn-primary w-full" 
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? t('registering') : t('btn_start_trial')}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};

// Business Login
const LoginPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/businesses/login`, formData);
      toast.success(t('welcome_back'));
      localStorage.setItem('suco_business', JSON.stringify(response.data.business));
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('invalid_credentials'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => navigate('/')}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <LanguageSelector />
      </div>
      
      <Card className="login-card">
        <CardHeader className="text-center">
          <SucoLogo size={60} />
          <CardTitle className="card-title">{t('login_title')}</CardTitle>
        </CardHeader>
        
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t('email')}</Label>
              <div className="input-icon">
                <Mail className="icon" />
                <Input
                  id="email"
                  type="email"
                  data-testid="login-email-input"
                  placeholder="email@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">{t('password')}</Label>
              <div className="input-icon">
                <Lock className="icon" />
                <Input
                  id="password"
                  type="password"
                  data-testid="login-password-input"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  required
                />
              </div>
            </div>
          </CardContent>
          
          <CardFooter className="flex flex-col gap-3">
            <Button 
              type="submit" 
              className="btn-primary w-full" 
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? t('logging_in') : t('btn_login')}
            </Button>
            <p className="text-sm text-muted-foreground">
              {t('no_account')}{' '}
              <Link to="/registro" className="text-primary font-medium">{t('sign_up')}</Link>
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};

// Business Dashboard
const DashboardPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [business, setBusiness] = useState(null);
  const [quejas, setQuejas] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [profileForm, setProfileForm] = useState({ email: '', email_cc: '', telefono: '', custom_logo_url: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsPeriod, setAnalyticsPeriod] = useState(30);
  const [wifiForm, setWifiForm] = useState({ ssid: '', password: '', encryption: 'WPA' });
  const [wifiQrUrl, setWifiQrUrl] = useState(null);
  const [generatingWifi, setGeneratingWifi] = useState(false);
  const [branches, setBranches] = useState([]);
  const [branchWifiForms, setBranchWifiForms] = useState({});
  const [branchWifiQrUrls, setBranchWifiQrUrls] = useState({});
  const [generatingBranchWifi, setGeneratingBranchWifi] = useState({});
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const loadDataRef = useRef(null);

  const loadData = useCallback(async (businessId) => {
    try {
      const [quejasRes, statsRes] = await Promise.all([
        axios.get(`${API}/businesses/${businessId}/quejas`),
        axios.get(`${API}/businesses/${businessId}/stats`)
      ]);
      setQuejas(quejasRes.data);
      setStats(statsRes.data);
      // Load branches for enterprise users
      try {
        const branchRes = await axios.get(`${API}/businesses/${businessId}/branches`);
        const branchList = branchRes.data.branches || [];
        setBranches(branchList);
        // Pre-fill WiFi forms for branches
        const forms = {};
        const qrUrls = {};
        branchList.forEach(b => {
          forms[b.id] = { ssid: b.wifi_ssid || '', password: b.wifi_password || '', encryption: b.wifi_encryption || 'WPA' };
          if (b.wifi_qr_url) qrUrls[b.id] = b.wifi_qr_url;
        });
        setBranchWifiForms(forms);
        setBranchWifiQrUrls(qrUrls);
      } catch (e) {
        // No branches or not enterprise account - expected fallback
        console.debug('[branches] not loaded:', e?.message);
      }
    } catch (error) {
      toast.error(t('error_loading'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  // Keep ref in sync so WebSocket callback always has latest version
  useEffect(() => {
    loadDataRef.current = loadData;
  }, [loadData]);

  const connectWebSocket = useCallback((businessId) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    
    const ws = new WebSocket(`${WS_URL}/ws/notifications/${businessId}`);
    
    ws.onopen = () => {
      // Send ping every 30 seconds to keep alive
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send('ping');
        }
      }, 30000);
      ws.pingInterval = pingInterval;
    };
    
    ws.onmessage = (event) => {
      if (event.data === 'pong') return;
      
      try {
        const notification = JSON.parse(event.data);
        if (notification.type === 'new_queja' && notificationsEnabled) {
          const { categoria, descripcion, nombre } = notification.data;
          
          // Play notification sound (optional)
          try {
            const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleRkAQIvO5765aw0AMn7O8NmxcgsAJnTH8t+2dg0AHWvB8+K9fA8AFmO78OS/fw8AEF248OXBgg8AC1q08efDhQ4ABla08ufEhg0AAla08ufEhwwAAla08ufEhwsAAla08ufEhwsAAla08ufEhwsAAla08ufEhwsAAla08ufEhwsAAla08ufEhwsAAla08ufEhwsA');
            audio.volume = 0.3;
            audio.play();
          } catch (e) {
            // Audio notification silently fails on some devices (autoplay policy)
            console.debug('[notify] audio blocked:', e?.message);
          }
          
          // Show toast notification
          toast(
            <div className="notification-toast">
              <div className="notification-icon">
                {categoria === 'queja' ? (
                  <AlertCircle className="h-5 w-5 text-red-500" />
                ) : (
                  <Lightbulb className="h-5 w-5 text-yellow-500" />
                )}
              </div>
              <div className="notification-content">
                <p className="notification-title">
                  Nueva {categoria === 'queja' ? 'queja' : 'sugerencia'}
                </p>
                <p className="notification-body">{descripcion}</p>
                <p className="notification-from">De: {nombre}</p>
              </div>
            </div>,
            {
              duration: 8000,
              icon: <BellRing className="h-5 w-5 text-blue-500" />,
            }
          );
          
          // Reload data to show new queja
          if (loadDataRef.current) loadDataRef.current(businessId);
        }
      } catch (e) {
        // Non-critical: notification parse failed
        console.debug('[ws] notification parse failed:', e?.message);
      }
    };
    
    ws.onclose = () => {
      if (ws.pingInterval) clearInterval(ws.pingInterval);
      
      // Reconnect after 3 seconds
      reconnectTimeoutRef.current = setTimeout(() => {
        connectWebSocket(businessId);
      }, 3000);
    };
    
    ws.onerror = () => {
    };
    
    wsRef.current = ws;
  }, [notificationsEnabled]);

  useEffect(() => {
    const storedBusiness = localStorage.getItem('suco_business');
    if (!storedBusiness) {
      navigate('/login');
      return;
    }
    
    const bizData = JSON.parse(storedBusiness);
    setBusiness(bizData);
    setProfileForm({
      email: bizData.email || '',
      email_cc: bizData.email_cc || '',
      telefono: bizData.telefono || '',
      custom_logo_url: bizData.custom_logo_url || ''
    });
    if (bizData.wifi_ssid) {
      setWifiForm({
        ssid: bizData.wifi_ssid || '',
        password: bizData.wifi_password || '',
        encryption: bizData.wifi_encryption || 'WPA'
      });
      if (bizData.wifi_qr_url) setWifiQrUrl(bizData.wifi_qr_url);
    }
    loadData(bizData.id);
    connectWebSocket(bizData.id);
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [navigate, connectWebSocket, loadData]);

  const loadAnalytics = useCallback(async (businessId, days) => {
    try {
      const res = await axios.get(`${API}/businesses/${businessId}/analytics?days=${days}`);
      setAnalytics(res.data);
    } catch (error) {
      toast.error(t('error_loading'));
    }
  }, []);

  useEffect(() => {
    if (business) {
      loadAnalytics(business.id, analyticsPeriod);
    }
  }, [business, analyticsPeriod, loadAnalytics]);

  const handleLogout = () => {
    if (wsRef.current) {
      wsRef.current.close();
    }
    localStorage.removeItem('suco_business');
    navigate('/');
  };

  const toggleNotifications = () => {
    setNotificationsEnabled(!notificationsEnabled);
    toast.success(notificationsEnabled ? 'Notificaciones desactivadas' : 'Notificaciones activadas');
  };

  const updateQuejaStatus = async (quejaId, newStatus) => {
    try {
      await axios.patch(`${API}/quejas/${quejaId}/estado?estado=${newStatus}`);
      toast.success(t('status_updated'));
      loadData(business.id);
    } catch (error) {
      toast.error(t('error_updating'));
    }
  };

  const handleProfileLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Solo se permiten imágenes JPG, PNG o WEBP');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('El archivo no puede superar 5MB');
      return;
    }
    setUploadingLogo(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const response = await axios.post(`${API}/upload/logo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setProfileForm(prev => ({ ...prev, custom_logo_url: `${BACKEND_URL}${response.data.logo_url}` }));
      toast.success('Logo subido');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const response = await axios.patch(`${API}/businesses/${business.id}`, {
        email: profileForm.email,
        email_cc: profileForm.email_cc || '',
        telefono: profileForm.telefono,
        custom_logo_url: profileForm.custom_logo_url
      });
      const updatedBiz = response.data;
      setBusiness(updatedBiz);
      localStorage.setItem('suco_business', JSON.stringify(updatedBiz));
      toast.success(t('profile_updated'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error_updating'));
    } finally {
      setSavingProfile(false);
    }
  };

  const [regeneratingQR, setRegeneratingQR] = useState(false);

  const handleRegenerateQR = async () => {
    setRegeneratingQR(true);
    try {
      const response = await axios.post(`${API}/businesses/${business.id}/regenerate-qr`);
      const updatedBiz = { ...business, ...response.data };
      setBusiness(updatedBiz);
      localStorage.setItem('suco_business', JSON.stringify(updatedBiz));
      toast.success(t('qr_regenerated'));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al regenerar QR');
    } finally {
      setRegeneratingQR(false);
    }
  };

  const getStatusBadge = (estado) => {
    const statusConfig = {
      pendiente: { variant: 'default', icon: Clock, label: t('status_pending') },
      en_proceso: { variant: 'secondary', icon: AlertCircle, label: t('status_in_progress') },
      resuelto: { variant: 'success', icon: CheckCircle, label: t('status_resolved') },
      cerrado: { variant: 'outline', icon: CheckCircle, label: t('status_closed') }
    };
    const config = statusConfig[estado] || statusConfig.pendiente;
    return (
      <Badge variant={config.variant} className="status-badge">
        <config.icon className="h-3 w-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const chartTickSmall = { fontSize: 11 };
  const chartTickMedium = { fontSize: 12 };

  const handleGenerateWifiQR = async () => {
    if (!wifiForm.ssid.trim()) {
      toast.error(t('wifi_ssid_required'));
      return;
    }
    setGeneratingWifi(true);
    try {
      const response = await axios.post(`${API}/wifi-qr/generate`, {
        ssid: wifiForm.ssid,
        password: wifiForm.password,
        encryption: wifiForm.encryption,
        business_id: business.id
      });
      setWifiQrUrl(response.data.qr_url);
      // Update stored business data with WiFi config
      const updatedBiz = { 
        ...business, 
        wifi_ssid: wifiForm.ssid, 
        wifi_password: wifiForm.password,
        wifi_encryption: wifiForm.encryption,
        wifi_qr_url: response.data.qr_url 
      };
      setBusiness(updatedBiz);
      localStorage.setItem('suco_business', JSON.stringify(updatedBiz));
      toast.success(t('wifi_qr_generated'));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setGeneratingWifi(false);
    }
  };

  const handleGenerateBranchWifiQR = async (branchId) => {
    const form = branchWifiForms[branchId];
    if (!form || !form.ssid.trim()) {
      toast.error(t('wifi_ssid_required'));
      return;
    }
    setGeneratingBranchWifi(prev => ({ ...prev, [branchId]: true }));
    try {
      const response = await axios.post(`${API}/branches/${branchId}/wifi-qr`, {
        ssid: form.ssid,
        password: form.password,
        encryption: form.encryption,
        branch_id: branchId,
        business_id: business.id
      });
      setBranchWifiQrUrls(prev => ({ ...prev, [branchId]: response.data.qr_url }));
      toast.success(t('wifi_qr_generated'));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setGeneratingBranchWifi(prev => ({ ...prev, [branchId]: false }));
    }
  };

  if (loading || !business) {
    return <div className="loading-container">{t('loading')}</div>;
  }

  // Calculate trial days remaining
  const getTrialDaysLeft = () => {
    if (business.subscription_status !== 'trial' || !business.trial_ends_at) return -1;
    const trialEnd = new Date(business.trial_ends_at);
    const now = new Date();
    const diff = Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const trialDaysLeft = getTrialDaysLeft();
  const trialExpired = business.subscription_status === 'trial' && trialDaysLeft <= 0;

  // Cancel subscription handler
  const handleCancelSubscription = async () => {
    if (!window.confirm(t('cancel_confirm'))) return;
    try {
      await axios.post(`${API}/businesses/${business.id}/cancel-subscription`);
      const updatedBiz = { ...business, subscription_status: 'cancelled' };
      setBusiness(updatedBiz);
      localStorage.setItem('suco_business', JSON.stringify(updatedBiz));
      toast.success(t('subscription_cancelled'));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  // If trial expired, show upgrade overlay
  if (trialExpired) {
    return (
      <div className="dashboard-container" data-testid="trial-expired">
        <header className="dashboard-header">
          <div className="header-left">
            <SucoLogo size={40} />
            <div>
              <h1 className="header-title">{business.nombre}</h1>
            </div>
          </div>
          <div className="header-right">
            <Button variant="ghost" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="h-4 w-4 mr-2" />
              {t('btn_logout')}
            </Button>
          </div>
        </header>
        <div className="trial-expired-overlay">
          <Card className="trial-expired-card">
            <CardContent className="trial-expired-content">
              <Clock className="h-16 w-16 text-orange-500 mx-auto mb-4" />
              <h2 data-testid="trial-expired-title">{t('trial_expired')}</h2>
              <p>{t('trial_expired_msg')}</p>
              <Button 
                className="btn-primary mt-6"
                onClick={() => navigate('/subscription')}
                data-testid="subscribe-now-btn"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                {t('btn_subscribe')}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container" data-testid="dashboard">
      <header className="dashboard-header">
        <div className="header-left">
          <SucoLogo size={40} />
          <div>
            <h1 className="header-title">{business.nombre}</h1>
            <p className="header-subtitle">{t('dashboard_subtitle')}</p>
          </div>
        </div>
        <div className="header-right">
          {business.subscription_status === 'trial' && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/subscription')}
              className="trial-button"
              data-testid="upgrade-btn"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              {t('free_trial')}
            </Button>
          )}
          <Button 
            variant="ghost" 
            size="icon"
            onClick={toggleNotifications}
            data-testid="notifications-toggle"
            className={notificationsEnabled ? 'notification-active' : ''}
          >
            {notificationsEnabled ? (
              <BellRing className="h-5 w-5" />
            ) : (
              <Bell className="h-5 w-5" />
            )}
          </Button>
          <Button variant="ghost" onClick={handleLogout} data-testid="logout-btn">
            <LogOut className="h-4 w-4 mr-2" />
            {t('btn_logout')}
          </Button>
          {(business.email === 'esg95@hotmail.com' || business.emailContacto === 'esg95@hotmail.com') && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/admin')}
              className="admin-link-btn"
              data-testid="admin-panel-link"
            >
              <BarChart3 className="h-4 w-4 mr-2" />
              Panel Admin
            </Button>
          )}
        </div>
      </header>

      {/* Trial Banner */}
      {business.subscription_status === 'trial' && trialDaysLeft > 0 && (
        <div className="trial-banner" data-testid="trial-banner">
          <div className="trial-banner-text">
            <Clock className="h-4 w-4" />
            <span>
              {trialDaysLeft === 1 
                ? t('trial_banner_last_day') 
                : t('trial_banner').replace('{days}', trialDaysLeft)}
            </span>
          </div>
          <Button 
            size="sm" 
            className="trial-banner-btn"
            onClick={() => navigate('/subscription')}
            data-testid="trial-subscribe-btn"
          >
            {t('btn_subscribe')}
          </Button>
        </div>
      )}

      {/* Active Subscription Banner */}
      {business.subscription_status === 'active' && (
        <div className="subscription-banner" data-testid="subscription-banner">
          <div className="trial-banner-text">
            <CheckCircle className="h-4 w-4" />
            <span>{t('subscription_active')}</span>
          </div>
          <Button 
            variant="ghost"
            size="sm"
            className="cancel-sub-btn"
            onClick={handleCancelSubscription}
            data-testid="cancel-subscription-btn"
          >
            {t('cancel_subscription')}
          </Button>
        </div>
      )}

      <main className="dashboard-main">
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="tabs-list">
            <TabsTrigger value="overview" data-testid="tab-overview">
              <BarChart3 className="h-4 w-4 mr-2" />
              {t('tab_overview')}
            </TabsTrigger>
            <TabsTrigger value="quejas" data-testid="tab-quejas">
              <MessageSquare className="h-4 w-4 mr-2" />
              {t('tab_complaints')}
            </TabsTrigger>
            <TabsTrigger value="qr" data-testid="tab-qr">
              <QrCode className="h-4 w-4 mr-2" />
              {t('tab_qr')}
            </TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics">
              <TrendingUp className="h-4 w-4 mr-2" />
              {t('tab_analytics')}
            </TabsTrigger>
            {business.plan_type === 'enterprise' && (
              <TabsTrigger value="branches" data-testid="tab-branches">
                <Building2 className="h-4 w-4 mr-2" />
                {t('tab_branches')}
              </TabsTrigger>
            )}
            <TabsTrigger value="profile" data-testid="tab-profile">
              <User className="h-4 w-4 mr-2" />
              {t('tab_profile')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="tab-content">
            {stats && (
              <div className="stats-grid">
                <Card className="stat-card">
                  <CardContent className="stat-content">
                    <div className="stat-icon blue">
                      <MessageSquare className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="stat-label">{t('stat_total')}</p>
                      <p className="stat-value">{stats.total}</p>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="stat-card">
                  <CardContent className="stat-content">
                    <div className="stat-icon yellow">
                      <Clock className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="stat-label">{t('stat_pending')}</p>
                      <p className="stat-value">{stats.pendientes}</p>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="stat-card">
                  <CardContent className="stat-content">
                    <div className="stat-icon green">
                      <CheckCircle className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="stat-label">{t('stat_resolved')}</p>
                      <p className="stat-value">{stats.resueltos}</p>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="stat-card">
                  <CardContent className="stat-content">
                    <div className="stat-icon red">
                      <AlertCircle className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="stat-label">{t('stat_complaints')}</p>
                      <p className="stat-value">{stats.quejas}</p>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="stat-card">
                  <CardContent className="stat-content">
                    <div className="stat-icon purple">
                      <Lightbulb className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="stat-label">{t('stat_suggestions')}</p>
                      <p className="stat-value">{stats.sugerencias}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          <TabsContent value="quejas" className="tab-content">
            <div className="quejas-list">
              {quejas.length === 0 ? (
                <Card className="empty-state">
                  <CardContent className="text-center py-12">
                    <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-medium">{t('no_messages')}</h3>
                    <p className="text-muted-foreground">{t('share_qr')}</p>
                  </CardContent>
                </Card>
              ) : (
                quejas.map((queja) => (
                  <Card key={queja.id} className="queja-card" data-testid={`queja-${queja.id}`}>
                    <CardHeader className="queja-header">
                      <div className="queja-meta">
                        <Badge variant={queja.categoria === 'queja' ? 'destructive' : 'default'}>
                          {queja.categoria === 'queja' ? (
                            <><AlertCircle className="h-3 w-3 mr-1" /> {t('complaint')}</>
                          ) : (
                            <><Lightbulb className="h-3 w-3 mr-1" /> {t('suggestion')}</>
                          )}
                        </Badge>
                        {getStatusBadge(queja.estado)}
                      </div>
                      <p className="queja-date">
                        {new Date(queja.created_at).toLocaleDateString(undefined, {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </CardHeader>
                    <CardContent>
                      <p className="queja-descripcion">{queja.descripcion}</p>
                      {(queja.nombre || queja.email || queja.telefono) && (
                        <div className="queja-contacto">
                          {queja.nombre && <span>{queja.nombre}</span>}
                          {queja.email && <span>{queja.email}</span>}
                          {queja.telefono && <span>{queja.telefono}</span>}
                        </div>
                      )}
                    </CardContent>
                    <CardFooter className="queja-footer">
                      <div className="queja-status-control">
                        <span className="queja-status-label">{t('change_status') || 'Cambiar estado'}:</span>
                        <Select 
                          value={queja.estado}
                          onValueChange={(value) => updateQuejaStatus(queja.id, value)}
                        >
                          <SelectTrigger className="queja-status-trigger" data-testid={`status-select-${queja.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pendiente">{t('status_pending')}</SelectItem>
                            <SelectItem value="en_proceso">{t('status_in_progress')}</SelectItem>
                            <SelectItem value="resuelto">{t('status_resolved')}</SelectItem>
                            <SelectItem value="cerrado">{t('status_closed')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </CardFooter>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="qr" className="tab-content">
            <Card className="qr-card">
              <CardHeader className="text-center">
                <CardTitle>{t('qr_title')}</CardTitle>
                <CardDescription>{t('qr_subtitle')}</CardDescription>
              </CardHeader>
              <CardContent className="qr-content">
                {business.qr_url && (
                  <img 
                    src={`${BACKEND_URL}${business.qr_url}`} 
                    alt="QR Code"
                    className="qr-image"
                    data-testid="qr-image"
                  />
                )}
                
                <div className="qr-link">
                  <p className="text-sm text-muted-foreground">{t('public_url')}</p>
                  <code className="qr-url">{`${window.location.origin}/b/${business.slug}`}</code>
                </div>
              </CardContent>
              <CardFooter className="qr-downloads">
                <Button 
                  className="btn-primary"
                  onClick={handleRegenerateQR}
                  disabled={regeneratingQR}
                  data-testid="regenerate-qr-btn"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${regeneratingQR ? 'animate-spin' : ''}`} />
                  {regeneratingQR ? t('saving') : t('regenerate_qr')}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => window.open(`${BACKEND_URL}${business.qr_url}`, '_blank')}
                  data-testid="download-qr-btn"
                >
                  <QrCode className="h-4 w-4 mr-2" />
                  {t('download_qr')}
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => window.open(`${BACKEND_URL}${business.poster_pdf_url}`, '_blank')}
                  data-testid="download-pdf-btn"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  {t('download_pdf')}
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => window.open(`${BACKEND_URL}${business.poster_docx_url}`, '_blank')}
                  data-testid="download-docx-btn"
                >
                  <Download className="h-4 w-4 mr-2" />
                  {t('download_docx')}
                </Button>
              </CardFooter>
            </Card>

            {/* WiFi QR Code Generator */}
            <Card className="wifi-qr-card" data-testid="wifi-qr-card">
              <CardHeader>
                <CardTitle className="wifi-qr-title">
                  <Wifi className="h-5 w-5" />
                  {t('wifi_qr_title')}
                </CardTitle>
                <CardDescription>{t('wifi_qr_subtitle')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="wifi-form">
                  <div className="wifi-form-field">
                    <Label htmlFor="wifi-ssid">{t('wifi_network_name')}</Label>
                    <Input
                      id="wifi-ssid"
                      value={wifiForm.ssid}
                      onChange={(e) => setWifiForm({ ...wifiForm, ssid: e.target.value })}
                      placeholder="Mi Red WiFi"
                      data-testid="wifi-ssid-input"
                    />
                  </div>
                  <div className="wifi-form-field">
                    <Label htmlFor="wifi-password">{t('wifi_password')}</Label>
                    <Input
                      id="wifi-password"
                      type="text"
                      value={wifiForm.password}
                      onChange={(e) => setWifiForm({ ...wifiForm, password: e.target.value })}
                      placeholder={t('wifi_password_placeholder')}
                      data-testid="wifi-password-input"
                    />
                  </div>
                  <div className="wifi-form-field">
                    <Label htmlFor="wifi-encryption">{t('wifi_security')}</Label>
                    <Select
                      value={wifiForm.encryption}
                      onValueChange={(value) => setWifiForm({ ...wifiForm, encryption: value })}
                    >
                      <SelectTrigger data-testid="wifi-encryption-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="WPA">WPA/WPA2</SelectItem>
                        <SelectItem value="WEP">WEP</SelectItem>
                        <SelectItem value="nopass">{t('wifi_no_password')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    className="btn-primary w-full"
                    onClick={handleGenerateWifiQR}
                    disabled={generatingWifi}
                    data-testid="generate-wifi-qr-btn"
                  >
                    <Wifi className={`h-4 w-4 mr-2 ${generatingWifi ? 'animate-spin' : ''}`} />
                    {generatingWifi ? t('saving') : t('wifi_generate_btn')}
                  </Button>
                </div>

                {wifiQrUrl && (
                  <div className="wifi-qr-result" data-testid="wifi-qr-result">
                    <img
                      src={`${BACKEND_URL}${wifiQrUrl}`}
                      alt="WiFi QR"
                      className="wifi-qr-image"
                      data-testid="wifi-qr-image"
                    />
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => window.open(`${BACKEND_URL}${wifiQrUrl}`, '_blank')}
                      data-testid="download-wifi-qr-btn"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      {t('wifi_download_qr')}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="analytics" className="tab-content">
            <div className="analytics-container">
              <div className="analytics-header">
                <div>
                  <h2 className="analytics-main-title" data-testid="analytics-title">{t('analytics_title')}</h2>
                  <p className="text-muted-foreground">{t('analytics_subtitle')}</p>
                </div>
                <div className="analytics-period-selector">
                  {[7, 30, 90].map((d) => (
                    <Button
                      key={d}
                      variant={analyticsPeriod === d ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setAnalyticsPeriod(d)}
                      data-testid={`period-${d}d-btn`}
                      className={analyticsPeriod === d ? 'btn-primary' : ''}
                    >
                      {t(`period_${d}d`)}
                    </Button>
                  ))}
                </div>
              </div>

              {analytics && analytics.total_in_period > 0 ? (
                <>
                  <Card className="analytics-chart-card">
                    <CardHeader>
                      <CardTitle className="text-base">{t('trend_chart_title')}</CardTitle>
                      <CardDescription>{t('total_in_period')}: {analytics.total_in_period}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="chart-wrapper" data-testid="trend-chart">
                        <ResponsiveContainer width="100%" height={320}>
                          <LineChart data={analytics.trends}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                            <XAxis
                              dataKey="date"
                              tick={chartTickSmall}
                              tickFormatter={(v) => {
                                const d = new Date(v + 'T00:00:00');
                                return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
                              }}
                              interval={(() => {
                                if (analyticsPeriod <= 7) return 0;
                                if (analyticsPeriod <= 30) return 4;
                                return 13;
                              })()}
                            />
                            <YAxis tick={chartTickMedium} allowDecimals={false} />
                            <Tooltip
                              labelFormatter={(v) => {
                                const d = new Date(v + 'T00:00:00');
                                return d.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
                              }}
                            />
                            <Legend />
                            <Line type="monotone" dataKey="quejas" name={t('complaints_label')} stroke="#E53E3E" strokeWidth={2} dot={analyticsPeriod <= 7} />
                            <Line type="monotone" dataKey="sugerencias" name={t('suggestions_label')} stroke="#D69E2E" strokeWidth={2} dot={analyticsPeriod <= 7} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>

                  {analytics.status_distribution.length > 0 && (
                    <Card className="analytics-chart-card">
                      <CardHeader>
                        <CardTitle className="text-base">{t('status_chart_title')}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="chart-wrapper status-chart-layout" data-testid="status-chart">
                          <ResponsiveContainer width="100%" height={280}>
                            <PieChart>
                              <Pie
                                data={analytics.status_distribution}
                                cx="50%"
                                cy="50%"
                                innerRadius={60}
                                outerRadius={100}
                                paddingAngle={4}
                                dataKey="value"
                                nameKey="name"
                                label={({ name, value }) => {
                                  const labels = { pendiente: t('status_pending'), en_proceso: t('status_in_progress'), resuelto: t('status_resolved'), cerrado: t('status_closed') };
                                  return `${labels[name] || name}: ${value}`;
                                }}
                              >
                                {analytics.status_distribution.map((entry) => {
                                  const colors = { pendiente: '#D69E2E', en_proceso: '#4A90D9', resuelto: '#38A169', cerrado: '#718096' };
                                  return <Cell key={entry.name} fill={colors[entry.name] || '#805AD5'} />;
                                })}
                              </Pie>
                              <Tooltip formatter={(value, name) => {
                                const labels = { pendiente: t('status_pending'), en_proceso: t('status_in_progress'), resuelto: t('status_resolved'), cerrado: t('status_closed') };
                                return [value, labels[name] || name];
                              }} />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </>
              ) : (
                <Card className="empty-state">
                  <CardContent className="text-center py-12">
                    <TrendingUp className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-medium">{t('no_data')}</h3>
                    <p className="text-muted-foreground">{t('share_qr')}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {business.plan_type === 'enterprise' && (
            <TabsContent value="branches" className="tab-content">
              <Card className="wifi-qr-card" data-testid="branches-wifi-card">
                <CardHeader>
                  <CardTitle className="wifi-qr-title">
                    <Building2 className="h-5 w-5" />
                    {t('branches_title')}
                  </CardTitle>
                  <CardDescription>{t('branches_subtitle')}</CardDescription>
                </CardHeader>
                <CardContent>
                  {branches.length === 0 ? (
                    <div className="empty-state" data-testid="branches-empty-state">
                      <Building2 className="h-12 w-12 text-muted-foreground" />
                      <p className="text-muted-foreground">{t('branches_no_branches')}</p>
                    </div>
                  ) : (
                    <div className="branches-grid">
                      {branches.map((branch) => {
                        const form = branchWifiForms[branch.id] || { ssid: '', password: '', encryption: 'WPA' };
                        const qrUrl = branchWifiQrUrls[branch.id];
                        const isGenerating = generatingBranchWifi[branch.id];
                        return (
                          <Card
                            key={branch.id}
                            className="branch-wifi-card"
                            data-testid={`branch-card-${branch.id}`}
                          >
                            <CardHeader>
                              <CardTitle className="text-base flex items-center gap-2">
                                <Wifi className="h-4 w-4" />
                                {branch.nombre}
                              </CardTitle>
                              <CardDescription>{t('branch_wifi_for')}</CardDescription>
                            </CardHeader>
                            <CardContent>
                              <div className="wifi-form">
                                <div className="wifi-form-field">
                                  <Label htmlFor={`branch-ssid-${branch.id}`}>{t('wifi_network_name')}</Label>
                                  <Input
                                    id={`branch-ssid-${branch.id}`}
                                    value={form.ssid}
                                    onChange={(e) => setBranchWifiForms((prev) => ({
                                      ...prev,
                                      [branch.id]: { ...form, ssid: e.target.value }
                                    }))}
                                    placeholder="Mi Red WiFi"
                                    data-testid={`branch-wifi-ssid-${branch.id}`}
                                  />
                                </div>
                                <div className="wifi-form-field">
                                  <Label htmlFor={`branch-pass-${branch.id}`}>{t('wifi_password')}</Label>
                                  <Input
                                    id={`branch-pass-${branch.id}`}
                                    type="text"
                                    value={form.password}
                                    onChange={(e) => setBranchWifiForms((prev) => ({
                                      ...prev,
                                      [branch.id]: { ...form, password: e.target.value }
                                    }))}
                                    placeholder={t('wifi_password_placeholder')}
                                    data-testid={`branch-wifi-pass-${branch.id}`}
                                  />
                                </div>
                                <div className="wifi-form-field">
                                  <Label htmlFor={`branch-enc-${branch.id}`}>{t('wifi_security')}</Label>
                                  <Select
                                    value={form.encryption}
                                    onValueChange={(value) => setBranchWifiForms((prev) => ({
                                      ...prev,
                                      [branch.id]: { ...form, encryption: value }
                                    }))}
                                  >
                                    <SelectTrigger data-testid={`branch-wifi-enc-${branch.id}`}>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="WPA">WPA/WPA2</SelectItem>
                                      <SelectItem value="WEP">WEP</SelectItem>
                                      <SelectItem value="nopass">{t('wifi_no_password')}</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <Button
                                  className="btn-primary w-full"
                                  onClick={() => handleGenerateBranchWifiQR(branch.id)}
                                  disabled={isGenerating}
                                  data-testid={`generate-branch-wifi-btn-${branch.id}`}
                                >
                                  <Wifi className={`h-4 w-4 mr-2 ${isGenerating ? 'animate-spin' : ''}`} />
                                  {isGenerating ? t('saving') : t('wifi_generate_btn')}
                                </Button>
                              </div>
                              {qrUrl && (
                                <div className="wifi-qr-result" data-testid={`branch-wifi-qr-result-${branch.id}`}>
                                  <img
                                    src={`${BACKEND_URL}${qrUrl}`}
                                    alt={`WiFi QR ${branch.nombre}`}
                                    className="wifi-qr-image"
                                    data-testid={`branch-wifi-qr-image-${branch.id}`}
                                  />
                                  <Button
                                    variant="outline"
                                    className="w-full"
                                    onClick={() => window.open(`${BACKEND_URL}${qrUrl}`, '_blank')}
                                    data-testid={`download-branch-wifi-btn-${branch.id}`}
                                  >
                                    <Download className="h-4 w-4 mr-2" />
                                    {t('wifi_download_qr')}
                                  </Button>
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          <TabsContent value="profile" className="tab-content">
            <Card className="profile-card">
              <CardHeader>
                <CardTitle data-testid="profile-title">{t('profile_title')}</CardTitle>
                <CardDescription>{t('profile_subtitle')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="profile-logo-section">
                  <Label>{t('current_logo')}</Label>
                  <div className="profile-logo-preview">
                    {profileForm.custom_logo_url ? (
                      <img src={profileForm.custom_logo_url} alt="Logo" className="profile-logo-img" data-testid="profile-logo-img" />
                    ) : (
                      <div className="profile-logo-placeholder" data-testid="profile-no-logo">
                        <Building2 className="h-10 w-10 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">{t('no_logo')}</span>
                      </div>
                    )}
                    <div className="profile-logo-actions">
                      <label className="logo-upload-btn">
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          onChange={handleProfileLogoUpload}
                          style={{ display: 'none' }}
                          data-testid="profile-logo-input"
                        />
                        <Button type="button" variant="outline" size="sm" disabled={uploadingLogo} asChild>
                          <span>
                            <Upload className="h-4 w-4 mr-2" />
                            {uploadingLogo ? t('saving') : t('change_logo')}
                          </span>
                        </Button>
                      </label>
                      {profileForm.custom_logo_url && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setProfileForm(prev => ({ ...prev, custom_logo_url: '' }))}
                          data-testid="profile-remove-logo"
                        >
                          {t('remove')}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="profile-email">{t('email')}</Label>
                  <div className="input-icon">
                    <Mail className="icon" />
                    <Input
                      id="profile-email"
                      type="email"
                      data-testid="profile-email-input"
                      value={profileForm.email}
                      onChange={(e) => setProfileForm(prev => ({ ...prev, email: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="profile-email-cc">{t('email_cc')}</Label>
                  <div className="input-icon">
                    <Mail className="icon" />
                    <Input
                      id="profile-email-cc"
                      type="email"
                      data-testid="profile-email-cc-input"
                      placeholder="cc@ejemplo.com"
                      value={profileForm.email_cc}
                      onChange={(e) => setProfileForm(prev => ({ ...prev, email_cc: e.target.value }))}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">{t('email_cc_hint')}</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="profile-phone">{t('phone')}</Label>
                  <div className="input-icon">
                    <Phone className="icon" />
                    <Input
                      id="profile-phone"
                      data-testid="profile-phone-input"
                      value={profileForm.telefono}
                      onChange={(e) => setProfileForm(prev => ({ ...prev, telefono: e.target.value }))}
                    />
                  </div>
                </div>
              </CardContent>
              <CardFooter>
                <Button
                  className="btn-primary w-full"
                  onClick={handleSaveProfile}
                  disabled={savingProfile}
                  data-testid="profile-save-btn"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {savingProfile ? t('saving') : t('save_changes')}
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>
        </Tabs>
        <div className="dashboard-support" data-testid="dashboard-support">
          {business?.id === '6a6cb613-ca2c-4ea2-8cc5-c6c95bb5ecac' && (
            <>
              <a href={`${API}/assets/email-template`} className="dashboard-download-email" download data-testid="download-email-template">
                {t('download_email_template')}
              </a>
              <span className="dashboard-support-sep">|</span>
            </>
          )}
          <a href="mailto:info@sucomx.com">{t('support')}: info@sucomx.com</a>
        </div>
      </main>
    </div>
  );
};

// Public Feedback Form
const FeedbackPage = () => {
  const { slug } = useParams();
  const { t } = useLanguage();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    categoria: 'queja',
    descripcion: '',
    nombre: '',
    email: '',
    telefono: ''
  });

  useEffect(() => {
    loadBusiness();
  }, [slug]);

  const loadBusiness = async () => {
    try {
      const response = await axios.get(`${API}/b/${slug}`);
      setBusiness(response.data);
    } catch (error) {
      toast.error(t('business_not_found'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/quejas`, {
        ...formData,
        business_slug: slug
      });
      setSubmitted(true);
      toast.success(t('thank_you'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error_sending'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="loading-container">{t('loading')}</div>;
  }

  if (!business) {
    return (
      <div className="error-container">
        <h2>{t('business_not_found')}</h2>
        <Link to="/">
          <Button>{t('back_home')}</Button>
        </Link>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="success-container">
        <div className="success-content">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">{t('thank_you')}</h2>
          <p className="text-muted-foreground">{t('message_sent')}</p>
          <Button 
            className="mt-6"
            onClick={() => {
              setSubmitted(false);
              setFormData({
                categoria: 'queja',
                descripcion: '',
                nombre: '',
                email: '',
                telefono: ''
              });
            }}
          >
            {t('send_another')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="feedback-container" data-testid="feedback-page">
      <Card className="feedback-card">
        <CardHeader className="text-center">
          <SucoLogo size={60} />
          <CardTitle className="feedback-title">{business.nombre}</CardTitle>
          <CardDescription className="feedback-subtitle">
            {t('feedback_question')}
          </CardDescription>
        </CardHeader>
        
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>{t('category')}</Label>
              <Select 
                value={formData.categoria}
                onValueChange={(value) => setFormData({...formData, categoria: value})}
              >
                <SelectTrigger data-testid="category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="queja">
                    <div className="flex items-center">
                      <AlertCircle className="h-4 w-4 mr-2 text-red-500" />
                      {t('complaint')}
                    </div>
                  </SelectItem>
                  <SelectItem value="sugerencia">
                    <div className="flex items-center">
                      <Lightbulb className="h-4 w-4 mr-2 text-yellow-500" />
                      {t('suggestion')}
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="descripcion">{t('describe_problem')}</Label>
              <Textarea
                id="descripcion"
                data-testid="description-input"
                placeholder={t('tell_us')}
                value={formData.descripcion}
                onChange={(e) => setFormData({...formData, descripcion: e.target.value})}
                required
                minLength={10}
                rows={4}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="nombre">{t('name_optional')}</Label>
              <Input
                id="nombre"
                data-testid="name-input"
                placeholder={t('your_name')}
                value={formData.nombre}
                onChange={(e) => setFormData({...formData, nombre: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">{t('email_optional')}</Label>
              <Input
                id="email"
                type="email"
                data-testid="email-input"
                placeholder={t('email_placeholder')}
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="telefono">{t('phone_optional')}</Label>
              <Input
                id="telefono"
                data-testid="phone-input"
                placeholder={t('phone_placeholder')}
                value={formData.telefono}
                onChange={(e) => setFormData({...formData, telefono: e.target.value})}
              />
            </div>
          </CardContent>
          
          <CardFooter className="flex gap-3">
            <Button 
              type="submit" 
              className="btn-primary flex-1" 
              disabled={submitting}
              data-testid="submit-feedback-btn"
            >
              <Send className="h-4 w-4 mr-2" />
              {submitting ? t('sending') : t('btn_send')}
            </Button>
            <Button 
              type="button" 
              variant="outline"
              onClick={() => setFormData({
                categoria: 'queja',
                descripcion: '',
                nombre: '',
                email: '',
                telefono: ''
              })}
            >
              {t('btn_clear')}
            </Button>
          </CardFooter>
        </form>
      </Card>
      
      <p className="powered-by">{t('powered_by')}</p>
    </div>
  );
};

// Subscription Page
const SubscriptionPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState(null);
  const [business, setBusiness] = useState(null);
  const [branchCount, setBranchCount] = useState(2);

  const loadPlans = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/subscription/plans`);
      setPlans(response.data);
    } catch (error) {
      toast.error(t('error_loading'));
    }
  }, [t]);

  useEffect(() => {
    const storedBusiness = localStorage.getItem('suco_business');
    if (!storedBusiness) {
      navigate('/login');
      return;
    }
    setBusiness(JSON.parse(storedBusiness));
    loadPlans();
  }, [navigate, loadPlans]);

  const handleStripeCheckout = async (planId) => {
    setLoading(true);
    try {
      const payload = { plan_id: planId, origin_url: window.location.origin };
      if (planId.includes('enterprise')) payload.branches = branchCount;
      const response = await axios.post(`${API}/subscription/checkout`, payload);
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(t('error_payment'));
    } finally {
      setLoading(false);
    }
  };

  const handlePayPalCheckout = async (planId) => {
    try {
      const payload = { plan_id: planId, origin_url: window.location.origin };
      if (planId.includes('enterprise')) payload.branches = branchCount;
      const response = await axios.post(`${API}/paypal/create-order`, payload);
      if (response.data.approve_url) {
        window.location.href = response.data.approve_url;
      }
    } catch (error) {
      toast.error(t('error_payment'));
    }
  };

  if (!plans) {
    return <div className="loading-container">{t('loading_plans')}</div>;
  }

  return (
    <div className="subscription-container" data-testid="subscription-page">
      <div className="subscription-header">
        <Button variant="ghost" onClick={() => navigate('/dashboard')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('back')}
        </Button>
      </div>

      <div className="subscription-content">
        <SucoLogo size={80} />
        <h1 className="subscription-title">{t('choose_plan')}</h1>
        <p className="subscription-subtitle">{t('plan_subtitle')}</p>

        <div className="plans-grid">
          {plans.plans.map((plan) => (
            <Card key={plan.id} className={`plan-card ${plan.id === 'yearly' ? 'featured' : ''}`}>
              {plan.savings && <Badge className="savings-badge">{plan.savings} {t('savings')}</Badge>}
              <CardHeader>
                <CardTitle>{plan.name}</CardTitle>
                <div className="plan-price">
                  <span className="price">${plan.price}</span>
                  <span className="period">{plan.id === 'yearly' ? t('per_year') : t('per_month')}</span>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="features-list">
                  {plans.features.map((feature) => (
                    <li key={feature}>
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </CardContent>
              <CardFooter className="plan-footer-actions">
                <Button 
                  className="btn-primary w-full"
                  variant="default"
                  onClick={() => handleStripeCheckout(plan.id)}
                  disabled={loading}
                  data-testid={`subscribe-${plan.id}-btn`}
                >
                  {loading ? t('processing') : `${t('btn_subscribe')} (Stripe)`}
                </Button>
                <Button 
                  className="w-full paypal-btn"
                  variant="outline"
                  onClick={() => handlePayPalCheckout(plan.id)}
                  disabled={loading}
                  data-testid={`paypal-${plan.id}-btn`}
                >
                  {loading ? t('processing') : `${t('btn_subscribe')} (PayPal)`}
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>

        {/* Enterprise Plans Section */}
        {plans.enterprise_plans && (
          <div className="enterprise-section" data-testid="enterprise-section">
            <div className="enterprise-header">
              <Building2 className="h-8 w-8" style={{ color: 'var(--suco-blue)' }} />
              <h2 className="enterprise-title">Plan Empresarial</h2>
              <p className="enterprise-subtitle">Para negocios con m&uacute;ltiples sucursales</p>
            </div>

            <div className="branch-calculator" data-testid="branch-calculator">
              <Label htmlFor="branch-count">Sucursales</Label>
              <div className="branch-counter">
                <Button variant="outline" size="sm" onClick={() => setBranchCount(Math.max(2, branchCount - 1))} data-testid="branch-decrement">-</Button>
                <span className="branch-count-display" data-testid="branch-count-display">{branchCount}</span>
                <Button variant="outline" size="sm" onClick={() => setBranchCount(branchCount + 1)} data-testid="branch-increment">+</Button>
              </div>
            </div>

            <div className="plans-grid">
              {plans.enterprise_plans.map((plan) => {
                const isYearly = plan.id === 'enterprise_yearly';
                const extraBranches = Math.max(0, branchCount - (plan.includes_branches || 1));
                const totalPrice = plan.base_price + (extraBranches * plan.extra_branch_price);
                return (
                  <Card key={plan.id} className={`plan-card enterprise-card ${isYearly ? 'featured' : ''}`}>
                    {plan.savings && <Badge className="savings-badge">{plan.savings} {t('savings')}</Badge>}
                    <CardHeader>
                      <CardTitle>{plan.name}</CardTitle>
                      <div className="plan-price">
                        <span className="price">${totalPrice.toFixed(2)}</span>
                        <span className="period">{isYearly ? t('per_year') : t('per_month')}</span>
                      </div>
                      <p className="enterprise-price-detail">
                        Base: ${plan.base_price} + {extraBranches} extra x ${plan.extra_branch_price}
                      </p>
                    </CardHeader>
                    <CardContent>
                      <ul className="features-list">
                        {plans.enterprise_features.map((feature) => (
                          <li key={feature}>
                            <CheckCircle className="h-4 w-4 text-green-500" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                    <CardFooter className="plan-footer-actions">
                      <Button 
                        className="btn-primary w-full"
                        variant="default"
                        onClick={() => handleStripeCheckout(plan.id)}
                        disabled={loading}
                        data-testid={`subscribe-${plan.id}-btn`}
                      >
                        {loading ? t('processing') : `${t('btn_subscribe')} (Stripe)`}
                      </Button>
                      <Button 
                        className="w-full paypal-btn"
                        variant="outline"
                        onClick={() => handlePayPalCheckout(plan.id)}
                        disabled={loading}
                        data-testid={`paypal-${plan.id}-btn`}
                      >
                        {loading ? t('processing') : `${t('btn_subscribe')} (PayPal)`}
                      </Button>
                    </CardFooter>
                  </Card>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Subscription Success Page
const SubscriptionSuccessPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [status, setStatus] = useState('checking');
  
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');

    const pollPaymentStatus = async (sid, attempts = 0) => {
      if (attempts >= 5) {
        setStatus('timeout');
        return;
      }

      try {
        const response = await axios.get(`${API}/subscription/status/${sid}`);
        
        if (response.data.payment_status === 'paid') {
          const business = JSON.parse(localStorage.getItem('suco_business'));
          if (business) {
            await axios.post(`${API}/businesses/${business.id}/activate-subscription?session_id=${sid}`);
            business.subscription_status = 'active';
            localStorage.setItem('suco_business', JSON.stringify(business));
          }
          setStatus('success');
        } else if (response.data.status === 'expired') {
          setStatus('expired');
        } else {
          setTimeout(() => pollPaymentStatus(sid, attempts + 1), 2000);
        }
      } catch (error) {
        setStatus('error');
      }
    };

    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus('error');
    }
  }, []);

  return (
    <div className="success-container">
      <div className="success-content">
        {status === 'checking' && (
          <>
            <Clock className="h-16 w-16 text-blue-500 mx-auto mb-4 animate-pulse" />
            <h2 className="text-2xl font-bold mb-2">{t('verifying_payment')}</h2>
            <p className="text-muted-foreground">{t('wait_confirmation')}</p>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">{t('payment_success')}</h2>
            <p className="text-muted-foreground mb-6">{t('subscription_active')}</p>
            <Button className="btn-primary" onClick={() => navigate('/dashboard')}>
              {t('go_dashboard')}
            </Button>
          </>
        )}
        {(status === 'error' || status === 'timeout' || status === 'expired') && (
          <>
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">{t('payment_error')}</h2>
            <p className="text-muted-foreground mb-6">{t('payment_confirm_error')}</p>
            <Button variant="outline" onClick={() => navigate('/subscription')}>
              {t('try_again')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

function App() {
  return (
    <LanguageProvider>
      <div className="App">
        <Toaster position="top-center" richColors />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<PromoLandingPage />} />
            <Route path="/inicio" element={<LandingPage />} />
            <Route path="/registro" element={<RegisterPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/b/:slug" element={<FeedbackPage />} />
            <Route path="/subscription" element={<SubscriptionPage />} />
            <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
            <Route path="/subscription/cancel" element={<SubscriptionPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/delete-account" element={<DeleteAccountPage />} />
          </Routes>
        </BrowserRouter>
      </div>
    </LanguageProvider>
  );
}

export default App;
